/**
 * E018-S008-T004: Rollup Cell View
 *
 * Simplified implementation that delegates CrossDoc logic to the
 * centralized related-cell-values utility.
 */
import { uniReactRoot } from '@affine/component';
import {
  type Cell,
  type CellRenderProps,
  createIcon,
  type DataViewCellLifeCycle,
} from '@blocksuite/affine/blocks/database';
import { computed, type ReadonlySignal } from '@preact/signals-core';
import {
  forwardRef,
  type ForwardRefRenderFunction,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { useSignalValue } from '../../../../modules/doc-info/utils';
import {
  type CrossDocLoadInfo,
  getRelatedCellValues,
  loadRelatedCellValuesAsync,
  relatedValuesCacheVersion,
} from '../relation/related-cell-values';
import { AGGREGATION_META, computeRollupValue } from './aggregation';
import type {
  RollupCellJsonValue,
  RollupCellRawValue,
  RollupPropertyData,
} from './define';
import { rollupPropertyModelConfig } from './define';
import * as styles from './style.css';

/**
 * Manager for Rollup Cell logic
 */
class RollupManager {
  readonly cell: Cell<
    RollupCellRawValue,
    RollupCellJsonValue,
    RollupPropertyData
  >;
  readonly selectCurrentCell: (editing: boolean) => void;
  readonly isEditing: ReadonlySignal<boolean>;

  get propertyData() {
    return this.cell.property.data$.value;
  }

  get store() {
    return (this.cell.view as any).dataSource.doc;
  }

  get dataSource() {
    return (this.cell.view as any).dataSource;
  }

  // Info for async CrossDoc loading (set during computedValue evaluation)
  _crossDocLoadInfo: CrossDocLoadInfo | null = null;

  /**
   * Computed: The aggregated rollup value
   *
   * E018-S008-T004: Uses centralized getRelatedCellValues utility
   * instead of implementing CrossDoc logic directly.
   */
  computedValue = computed(() => {
    // Subscribe to cache updates
    void relatedValuesCacheVersion.value;

    const propertyData = this.propertyData;
    const { sourceRelationFieldId, targetFieldId, aggregation, separator } =
      propertyData;

    // Not configured
    if (!sourceRelationFieldId || !targetFieldId) {
      this._crossDocLoadInfo = null;
      return null;
    }

    // Get related row IDs from the source relation field
    const relationValue = this.dataSource.cellValueGet(
      this.cell.rowId,
      sourceRelationFieldId
    ) as string[] | null;

    if (!relationValue || relationValue.length === 0) {
      this._crossDocLoadInfo = null;
      return computeRollupValue([], aggregation, separator);
    }

    // Get relation property data (contains targetDatabaseId, targetDocId)
    const relationData = this.dataSource.propertyDataGet(
      sourceRelationFieldId
    ) as
      | {
          targetDatabaseId?: string;
          targetDocId?: string;
        }
      | undefined;

    if (!relationData?.targetDatabaseId) {
      this._crossDocLoadInfo = null;
      return null;
    }

    // E018-S008-T004: Use centralized utility for InDoc/CrossDoc handling
    const result = getRelatedCellValues(
      this.store,
      this.store.id,
      relationData,
      relationValue,
      targetFieldId
    );

    // Store load info for async loading in useEffect
    this._crossDocLoadInfo = result.loadInfo;

    // Aggregate and return
    return computeRollupValue(result.values, aggregation, separator);
  });

  constructor(
    props: CellRenderProps<
      RollupPropertyData,
      RollupCellRawValue,
      RollupCellJsonValue
    >
  ) {
    this.cell = props.cell;
    this.selectCurrentCell = props.selectCurrentCell;
    this.isEditing = props.isEditing$;
  }
}

/**
 * Rollup Cell Component (Read-Only)
 */
const RollupCellComponent: ForwardRefRenderFunction<
  DataViewCellLifeCycle,
  CellRenderProps<RollupPropertyData, RollupCellRawValue, RollupCellJsonValue>
> = (props, ref): ReactNode => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const manager = useMemo(() => new RollupManager(props), []);
  const [, setForceUpdate] = useState(0);

  useImperativeHandle(
    ref,
    () => ({
      beforeEnterEditMode: () => false, // Read-only, no editing
      beforeExitEditingMode: () => {},
      afterEnterEditingMode: () => {},
      focusCell: () => true,
      blurCell: () => true,
      forceUpdate: () => {},
    }),
    []
  );

  const computedValue = useSignalValue(manager.computedValue);
  const propertyData = manager.propertyData;
  const loadInfo = manager._crossDocLoadInfo;

  // E018-S008-T004: Load CrossDoc values asynchronously using centralized utility
  useEffect(() => {
    if (!loadInfo) return;

    let cancelled = false;

    const load = async () => {
      await loadRelatedCellValuesAsync(loadInfo);
      if (!cancelled) {
        setForceUpdate(prev => prev + 1);
      }
    };

    load().catch(console.error);

    return () => {
      cancelled = true;
    };
  }); // Run on every render to catch loadInfo changes

  // Unconfigured State
  if (!propertyData.sourceRelationFieldId || !propertyData.targetFieldId) {
    return (
      <div className={styles.cellContainer}>
        <span className={styles.unconfigured}>Configure rollup...</span>
      </div>
    );
  }

  // Empty State (no related rows)
  if (computedValue === null || computedValue === undefined) {
    return (
      <div className={styles.cellContainer}>
        <span className={styles.emptyState}>-</span>
      </div>
    );
  }

  // Array values (show_original, show_unique)
  if (Array.isArray(computedValue)) {
    const maxVisible = 3;
    const visibleItems = computedValue.slice(0, maxVisible);
    const remainingCount = computedValue.length - maxVisible;

    return (
      <div className={styles.cellContainer}>
        <div className={styles.arrayContainer}>
          {visibleItems.map((item, index) => (
            <span key={index} className={styles.arrayItem}>
              {item}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className={styles.moreCount}>+{remainingCount}</span>
          )}
        </div>
      </div>
    );
  }

  // Percent values
  if (
    propertyData.aggregation === 'percent_empty' ||
    propertyData.aggregation === 'percent_filled'
  ) {
    return (
      <div className={styles.cellContainer}>
        <span className={styles.percentValue}>{computedValue}</span>
      </div>
    );
  }

  // Number values
  if (typeof computedValue === 'number') {
    const formatted = Number.isInteger(computedValue)
      ? computedValue.toString()
      : computedValue.toFixed(2);

    const meta = AGGREGATION_META[propertyData.aggregation];

    return (
      <div className={styles.cellContainer}>
        <span className={styles.numberValue}>
          {meta?.icon && (
            <span style={{ marginRight: '4px' }}>{meta.icon}</span>
          )}
          {formatted}
        </span>
      </div>
    );
  }

  // String values (concatenate)
  return (
    <div className={styles.cellContainer}>
      <span>{String(computedValue)}</span>
    </div>
  );
};

const RollupCell = forwardRef(RollupCellComponent);

/**
 * Property Config - registers the Rollup type
 */
export const rollupPropertyConfig =
  rollupPropertyModelConfig.createPropertyMeta({
    icon: createIcon('ViewLayersIcon'),
    cellRenderer: {
      view: uniReactRoot.createUniComponent(RollupCell),
    },
  });
